import React, { useCallback, useEffect, useRef, useState } from 'react'
import classNames from 'classnames'
import { InfoBox } from '../../../../components/InfoBox/InfoBox'
import { executionHasNothingResolvable } from '../../../../store/crossClient/utils'
import { CrossClientHeaderPortal } from '../Header/CrossClientHeader'
import { CCQ_CARD_ORDER, CcqCards } from './ccqResultView'
import { CcqAiProvider } from './ccqAi'
import { CcqGroupCard } from './CcqGroupCard'
import { CcqMatchDetailsPanel } from './CcqMatchDetailsPanel'
import { CcqQueryDetails } from './CcqQueryDetails'
import { CcqStatusLine } from './CcqStatusLine'
import { CCQ_NOTHING_RESOLVABLE } from './ccqRunCopy'
import { CcqInitState } from './CcqInitState'
import { CcqWorkspaceProvider } from './ccqWorkspace'
import { CcqPhases, useCcqRun } from './useCcqRun'
import { useCcqCursor } from './useCcqListCursor'
import { CcqFullListTransitions, useCcqWorkspace } from './useCcqWorkspace'
import styles from './CcqRunView.module.scss'

const STATUS_COLLAPSE_MS = 5000

// Matches .initExit in CcqInitState.module.scss.
const INIT_EXIT_MS = 200

// Match the .staggerIn duration and the .staggerSlot* delays in CcqRunView.module.scss.
const STAGGER_STEP_MS = 70
const STAGGER_IN_MS = 320
const STAGGER_LAST_SLOT = 1 + CCQ_CARD_ORDER.length
const STAGGER_TOTAL_MS = STAGGER_LAST_SLOT * STAGGER_STEP_MS + STAGGER_IN_MS

interface CcqEnterSlotProps {
  slot: number
  isEntering: boolean
  isLeaving: boolean
  children: React.ReactNode
}

// The entry class is fixed at mount: swapping it on a mounted element would replay the animation as a flash.
const CcqEnterSlot: React.FC<CcqEnterSlotProps> = ({ slot, isEntering, isLeaving, children }) => {
  const [enterClass] = useState(() => (isEntering ? classNames(styles.staggerIn, styles[`staggerSlot${slot}`]) : styles.swapIn))

  return <div className={isLeaving ? styles.swapOut : enterClass}>{children}</div>
}

interface CcqRunViewProps {
  // When the init screen this page carries on from first showed (epoch ms).
  initShownSince?: number
}

const CcqRunLayout: React.FC<CcqRunViewProps> = ({ initShownSince }) => {
  const { execution, criteria, phase, isDone, statusCollapsed, collapseStatus, counts } = useCcqRun()
  const isInit = phase === CcqPhases.INIT
  const { panel, takeListCloseFocus } = useCcqWorkspace()
  const { card: fullListCard, swap: fullListTransition } = useCcqCursor().list
  const isOpeningFullList = fullListTransition?.kind === CcqFullListTransitions.OPENING
  const titleRef = useRef<HTMLHeadingElement>(null)

  // Runs after the cards' own effects: a closed list whose card has no See all to focus (it's empty, or left the grid) hands focus to the title.
  useEffect(() => {
    if (fullListCard === null && takeListCloseFocus()) {
      titleRef.current?.focus()
    }
  }, [fullListCard, takeListCloseFocus])

  // Lags isInit by INIT_EXIT_MS so the init block can play its exit animation.
  const [isInitGone, setIsInitGone] = useState(!isInit)
  // Only true during the initial stagger, so cards remounting later don't replay it.
  const [isEntering, setIsEntering] = useState(true)

  useEffect(() => {
    if (isInit || isInitGone) {
      return
    }
    const timer = setTimeout(() => {
      setIsInitGone(true)
    }, INIT_EXIT_MS)
    return () => {
      clearTimeout(timer)
    }
  }, [isInit, isInitGone])

  useEffect(() => {
    if (!isInitGone) {
      return
    }
    const timer = setTimeout(() => {
      setIsEntering(false)
    }, STAGGER_TOTAL_MS)
    return () => {
      clearTimeout(timer)
    }
  }, [isInitGone])
  const isPointerOverCards = useRef(false)
  const isCollapsePending = useRef(false)

  // Defer the collapse while the pointer is over the cards, so they don't move under it.
  const requestCollapse = useCallback(() => {
    if (isPointerOverCards.current) {
      isCollapsePending.current = true
      return
    }
    collapseStatus()
  }, [collapseStatus])

  useEffect(() => {
    if (!isDone || statusCollapsed) {
      return
    }
    const timer = setTimeout(requestCollapse, STATUS_COLLAPSE_MS)
    window.addEventListener('pointerdown', requestCollapse)
    window.addEventListener('keydown', requestCollapse)
    window.addEventListener('wheel', requestCollapse, { passive: true })
    return () => {
      clearTimeout(timer)
      window.removeEventListener('pointerdown', requestCollapse)
      window.removeEventListener('keydown', requestCollapse)
      window.removeEventListener('wheel', requestCollapse)
    }
  }, [isDone, statusCollapsed, requestCollapse])

  const handlePointerEnter = () => {
    isPointerOverCards.current = true
  }

  const handlePointerLeave = () => {
    isPointerOverCards.current = false
    if (isCollapsePending.current) {
      isCollapsePending.current = false
      collapseStatus()
    }
  }

  // Only AI Deep Analysis fills MISSING DATA; it's last, so its appearing shifts no other card.
  const shownCards = CCQ_CARD_ORDER.filter((card) => card !== CcqCards.MISSING_DATA || counts.missingData > 0)
  // An open full list keeps its card even at 0 clients (a restored link), so its close stays reachable.
  const cards = fullListCard === null ? shownCards : [fullListCard]

  const railContent = panel === null ? <CcqQueryDetails /> : <CcqMatchDetailsPanel />

  // The title and rail hold their space during init so nothing shifts when results arrive. They never
  // remount, so they keep their stagger class for good.
  return (
    <div className='truly-full-size-content'>
      <CrossClientHeaderPortal execution={execution} />
      <div className={classNames(styles.content, 'position-relative zocks-px-2.0 pt-5 zocks-pb-2')}>
        {isInitGone ? null : <CcqInitState question={execution.userQuestion} criteria={criteria} isExiting={!isInit} shownSince={initShownSince} />}
        <h1
          ref={titleRef}
          tabIndex={-1}
          className={classNames('fs-5 fw-bold lh-sm zocks-text-gray-900 mb-5', isInitGone ? [styles.staggerIn, styles.staggerSlot0] : 'invisible')}
          aria-hidden={isInitGone ? undefined : true}
        >
          {execution.userQuestion}
        </h1>
        {/* data-ccq-grid: measured by useFillToViewportBottom. */}
        <div className={styles.grid} data-ccq-grid=''>
          <div className='d-flex flex-column mw-0'>
            {isInitGone ? (
              <>
                {fullListCard === null ? (
                  <CcqEnterSlot slot={1} isEntering={isEntering} isLeaving={isOpeningFullList}>
                    <CcqStatusLine isCollapsed={statusCollapsed} />
                  </CcqEnterSlot>
                ) : null}
                {executionHasNothingResolvable(execution) ? (
                  <div className='mb-3'>
                    <InfoBox variant='info'>{CCQ_NOTHING_RESOLVABLE}</InfoBox>
                  </div>
                ) : null}
                <div className='d-flex flex-column gap-3' onPointerEnter={handlePointerEnter} onPointerLeave={handlePointerLeave}>
                  {cards.map((card, index) => (
                    // A card below the first moves when its full list opens or closes; the key change
                    // remounts it so it fades in rather than jumping.
                    <CcqEnterSlot
                      key={card === CCQ_CARD_ORDER[0] || fullListCard === null ? card : `${card}-list`}
                      slot={2 + index}
                      isEntering={isEntering}
                      isLeaving={isOpeningFullList && fullListTransition?.card !== card}
                    >
                      <CcqGroupCard card={card} />
                    </CcqEnterSlot>
                  ))}
                </div>
              </>
            ) : null}
          </div>
          <aside className={classNames(styles.rail, isInitGone ? [styles.staggerIn, styles.staggerSlot1] : null)}>{isInitGone ? railContent : null}</aside>
        </div>
      </div>
    </div>
  )
}

export const CcqRunView: React.FC<CcqRunViewProps> = ({ initShownSince }) => {
  return (
    <CcqWorkspaceProvider>
      <CcqAiProvider>
        <CcqRunLayout initShownSince={initShownSince} />
      </CcqAiProvider>
    </CcqWorkspaceProvider>
  )
}
